import type { DOMProps } from "@simplybusiness/mobius";
import { useBreakpoint } from "@simplybusiness/mobius";
import type { RefAttributes } from "react";
import {
  useCurrentSection,
  useQuestionnaireNavigation,
  useQuestionnaire,
} from "../../../../features/questionnaire";
import { useShouldBlockSubmission } from "../../../../hooks/useShouldBlockSubmission";
import { SeeMoreCoversButton } from "./SeeMoreCovers";
import { QuestionnaireActions } from "../QuestionnaireActions";
import type { BaseQuestionnaireActionsProps, ActionsElement } from "../index";

export type CoverGeniusQuestionnaireActionsProps =
  BaseQuestionnaireActionsProps & DOMProps & RefAttributes<ActionsElement>;

/**
 * Cover Genius variant of questionnaire actions.
 * Includes "See more covers" button alongside standard navigation actions.
 * Layout differs between desktop and mobile.
 * Wraps StandardQuestionnaireActions with additional Cover Genius-specific UI.
 */
export const CoverGeniusQuestionnaireActions = ({
  ref,
}: CoverGeniusQuestionnaireActionsProps) => {
  // Access state via hooks
  const { up } = useBreakpoint();
  const isDesktop = up("md");
  const { handleSeeMoreCovers, showSeeMoreCoversTrigger } =
    useQuestionnaireNavigation();
  const { isFirstSection, isLastSection } = useCurrentSection();

  const { errors } = useQuestionnaire();
  const shouldBlockSubmission = useShouldBlockSubmission();
  const showSubmitAction = isLastSection;
  const shouldShowBackAction = !isFirstSection && !isDesktop;
  const shouldShowNextAction = !isLastSection && !showSubmitAction;

  const actionsContent = (
    <QuestionnaireActions ref={ref}>
      {shouldShowBackAction && <QuestionnaireActions.Back />}
      {showSubmitAction && (
        <QuestionnaireActions.Submit
          buttonLabel="Get selection"
          isDisabled={shouldBlockSubmission}
          errors={errors}
        />
      )}
      {shouldShowNextAction && (
        <QuestionnaireActions.Next label="Get selection" />
      )}
    </QuestionnaireActions>
  );

  const seeMoreButton = showSeeMoreCoversTrigger && (
    <SeeMoreCoversButton
      label="See more covers"
      onClick={handleSeeMoreCovers}
      desktop={isDesktop}
      showButton={showSeeMoreCoversTrigger}
    />
  );

  if (isDesktop) {
    return (
      <div
        className="cover-genius-actions-container"
        data-testid="desktop-actions-container"
      >
        {seeMoreButton}
        {actionsContent}
      </div>
    );
  }

  return (
    <>
      {seeMoreButton}
      {actionsContent}
    </>
  );
};

CoverGeniusQuestionnaireActions.displayName = "CoverGeniusQuestionnaireActions";
